import { fn } from '@ember/helper';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import { ensureSafeComponent } from '@embroider/util';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismIcon,
  PrismLoadingBar,
  PrismMessage,
  PrismTable,
  PrismTableCellsTdIcon,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, gt, not } from 'ember-truth-helpers';
import type { Hotel, Ota } from 'frontend-utils/models';
import GeneralDayDetailTabsTabDayDetailBase, {
  type GeneralDayDetailTabsTabDayDetailBaseSignature,
} from 'lumen/components/general/day-detail/tabs/tab-day-detail-base';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import GeneralModalFilterBar from 'lumen/components/general/modal-filter-bar';
import HotelPricingAssistantDayDetailTabsCompsetRecommendationReasons from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/compset-recommendation-reasons';
import HotelPricingAssistantDayDetailTabsEventsAndHolidaysList from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/events-and-holidays-list';
import HotelPricingAssistantDayDetailTabsOccupancyRecommendationReasons from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/occupancy-recommendation-reasons';
import HotelPricingAssistantDayDetailTabsUserRulesRecommendationReasons from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/user-rules-recommendation-reasons';
import HotelPricingAssistantEditPriceNumberInput from 'lumen/components/hotel/pricing-assistant/edit-price-number-input';
import HotelPricingAssistantRecommendationActionButtonTooltip from 'lumen/components/hotel/pricing-assistant/recommendation-action-button-tooltip';
import HotelPricingAssistantRecommendationActionState from 'lumen/components/hotel/pricing-assistant/recommendation-action-state';
import HotelPricingAssistantTableCellsTdWithBadge from 'lumen/components/hotel/pricing-assistant/table/cells/td-with-badge';
import type { RatesParams } from 'lumen/components/market/rates/tables/table-market-rates';
import displayRateParityFilters from 'lumen/helpers/display-rate-parity-filters';
import type { Carate, Day, RoomTypeRecommendation } from 'lumen/models';
import type HotelPricingManagerDetailRoute from 'lumen/routes/hotel/pricing-manager/detail';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  AUTOPILOT_DAY_ACTIONS,
  DETAIL_TAB_PRICING_TABS,
  EDIT_RECOMMENDED_PRICE_BUTTON_TYPE,
  PMS_SETTINGS_OFFSET_TYPES,
  PUSH_ERROR_CODES,
  PUSH_ERROR_CODES_TRANSLATION_KEY_MAPPING,
  RATE_TYPE_TO_TRANSLATION_KEYS,
  RECOMMENDATION_ACTION_STATES,
  RECOMMENDATIONACTIONS_ACTIONS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';
import { formatAutopilotPushMessage } from 'lumen/utils/pricing-assistant/autopilot-frequency-formatting';
import { isDayInsideAutopilotLeadtimeRange } from 'lumen/utils/pricing-assistant/is-day-inside-autopilot-leadtime-range';
import { trackAutopilotDayToggle } from 'lumen/utils/pricing-assistant/track-autopilot-day-toggle';
import { generateRecommendationPushData } from 'lumen/utils/pricing-assistant/usage-analytics-utils';
import type { ModelFrom } from 'lumen/utils/routes';
import { trackedReset } from 'tracked-toolbox';

import styles from './tab-pricing.module.css';

const ROOMS_TAB_COLUMN_VALUE_PATHS = {
  ROOM_TYPE: 'room_type',
  CURRENT_PRICE: 'current_price',
  RECOMMENDED_PRICE: 'recommended_price',
};

interface HotelPricingAssistantDayDetailTabsTabPricingSignature extends GeneralDayDetailTabsTabDayDetailBaseSignature {
  Args: {
    currentHotel: Hotel;
    date: string;
    dayModel: Day;
    filteredRatesParamsForPricingAssistant: {
      los: number;
      ota: string;
    };
    isEditActive: boolean;
    isLoading: boolean;
    model: ModelFrom<HotelPricingManagerDetailRoute>;
    ratesParams: RatesParams;
    toggleEdit: (value: boolean) => void;
  } & GeneralDayDetailTabsTabDayDetailBaseSignature['Args'];
}

export default class HotelPricingAssistantDayDetailTabsTabPricing extends GeneralDayDetailTabsTabDayDetailBase<HotelPricingAssistantDayDetailTabsTabPricingSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // By default, show calculation tab
  @tracked selectedTabId = DETAIL_TAB_PRICING_TABS.CALCULATION;
  DETAIL_TAB_PRICING_TABS = DETAIL_TAB_PRICING_TABS;
  EDIT_RECOMMENDED_PRICE_BUTTON_TYPE = EDIT_RECOMMENDED_PRICE_BUTTON_TYPE;
  // Reset new price value when the date changes
  @trackedReset('args.date') newPriceValue = this.newPriceInitialValue;

  RECOMMENDATIONACTIONS_ACTIONS = RECOMMENDATIONACTIONS_ACTIONS;
  triggerDropdownIconButton = GeneralDropdownsTriggersTriggerDropdownIconButton;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get autopilotDayAction() {
    if (
      this.carateObjectOfSelectedDay &&
      this.carateObjectOfSelectedDay.autopilotEnabled
    ) {
      return [
        {
          id: AUTOPILOT_DAY_ACTIONS.DISABLE,
          label: this.intl.t(
            'pricing_assistant.autopilot.label.disable_autopilot',
          ),
          icon: 'automation-off',
        },
      ];
    }

    return [
      {
        id: AUTOPILOT_DAY_ACTIONS.ENABLE,
        label: this.intl.t(
          'pricing_assistant.autopilot.label.enable_autopilot',
        ),
        icon: 'automation',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get autopilotDayActionValue() {
    return {
      icon: 'kebab',
      label: '',
    };
  }

  get autopilotPushMessageTranslation(): string {
    const autopilotFrequency =
      this.hotelAndMarketService.hotel.paAutopilotSettings?.settings.schedule;

    if (!autopilotFrequency) {
      return '';
    }

    return formatAutopilotPushMessage(
      this.hasRecommendation,
      autopilotFrequency,
      this.hotelAndMarketService,
      this.intl,
    );
  }

  get carateObjectOfSelectedDay(): Carate | undefined {
    return this.periodOfSelectedDay?.carates?.find((carate) => {
      return (
        carate.hotelinfo.id === this.hotelAndMarketService.hotel.hotelinfo.id
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get carecommendationOfSelectedDay() {
    return this.carateObjectOfSelectedDay?.carecommendation ?? null;
  }

  get editPriceButtonDisabled(): boolean {
    return (
      this.access.isPricingAssistantActionOrInputDisabled ||
      this.isAutopilotPriceBeingPushedOrIsPushed
    );
  }

  get hasRecommendation(): boolean {
    return Boolean(this.carecommendationOfSelectedDay);
  }

  get isAutopilotPriceBeingPushedOrIsPushed(): boolean {
    return (
      this.hasRecommendation &&
      Boolean(this.carecommendationOfSelectedDay?.isBeingPushedOrIsPushed) &&
      Boolean(this.carecommendationOfSelectedDay?.autopilotEnabled)
    );
  }

  /*
    Case 1: Day with recommendation and overwritten value -> Show overwritten value
    Case 2: Day with recommendation and no overwritten value -> Show recommended value
    Case 3: Day without recommendation -> Show no initial value
  */
  get newPriceInitialValue(): number | null {
    if (!this.carecommendationOfSelectedDay) {
      return null;
    }

    // Note: we currently use the formatRate function here (without currency), to make sure we end up with the exact same price as shown to the user in the UI
    // We could do the rounding separately here, but currently went with re-using the same format function
    // The parseInt is to make sure that we always compare numbers with numbers in the validation function

    // formatRate by default has format '0,0'. However, ',' is a non-numeric character which causes parseInt to stop parsing when encountered
    // For instance, parseInt('1,000', 10) will give 1 instead of expected 1000
    // Therefore, we override the format of formatRate to '0' to avoid any comma separators
    const formattedRate = this.hotelAndMarketService.formatRate(
      this.carecommendationOfSelectedDay.priceToShow,
      false,
      false,
      { maximumFractionDigits: 0, minimumFractionDigits: 0 },
    );

    if (!formattedRate) {
      return null;
    }

    return parseInt(formattedRate, 10);
  }

  /*
    New price is invalid:
    1. User is editing the price but the new price value is not set
    2. New price is less than booking offset value
  */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get newPriceInputFieldErrorState() {
    if (this.args.isEditActive) {
      if (!this.newPriceValue) {
        return {
          hasErrors: true,
        };
      }

      if (
        this.hotelAndMarketService.hotel.caxxxPmsSettings.pms_root_rate_offset
          .type === PMS_SETTINGS_OFFSET_TYPES.ABSOLUTE
      ) {
        const bookingOffset =
          this.hotelAndMarketService.hotel.caxxxPmsSettings.pms_root_rate_offset
            .value;
        const isNegativePricePushedToPMS = this.newPriceValue <= bookingOffset;

        if (isNegativePricePushedToPMS) {
          return {
            errorMessage: this.intl.t(
              'pricing_assistant.edit_price.error.negative_pms_rate',
              {
                value: this.hotelAndMarketService.formatRate(bookingOffset),
                htmlSafe: true,
              },
            ),
            hasErrors: true,
          };
        }
      }
    }

    return {
      hasErrors: false,
    };
  }

  // Note: Pricing tab contents are shown once all the data is requested. Therefore, we can safely assume that the model is always available here.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get periodOfSelectedDay() {
    return this.args.model.periods[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pushErrorMessageTranslation() {
    if (
      this.carecommendationOfSelectedDay &&
      Object.values(PUSH_ERROR_CODES).includes(
        this.carecommendationOfSelectedDay.pushResultMessage,
      )
    ) {
      const translationKey =
        PUSH_ERROR_CODES_TRANSLATION_KEY_MAPPING[
          this.carecommendationOfSelectedDay.pushResultMessage
        ];

      return translationKey ? this.intl.t(translationKey) : '';
    }

    return '';
  }

  get recommendedPriceFiltersTranslation(): string {
    const { los, ota: otaId, rateParam } = this.args.ratesParams;

    // The rate type shown in the filter bar is the Booking.com matching rate type selected in the PMS Settings
    // Extra check for the "value" to prevent a hard crash in case BE returns a rate type that is not supported by FE and thus doesn't have a mapped translation key
    const key = RATE_TYPE_TO_TRANSLATION_KEYS[rateParam];
    const selectedRateType = key ? this.intl.t(key) : rateParam;

    const selectedOTA = this.store.peekRecord<Ota>('ota', otaId);
    const selectedOTAName = selectedOTA ? selectedOTA.name : otaId;
    const selectedLos = this.intl.t('rates.nights', { count: los });

    // Rate type - OTA - LOS - Booking.com room name
    const result = `${selectedRateType} - ${selectedOTAName} - ${selectedLos}`;

    // Show actual room name for which the price is shown instead of preferred rate room name (it can be different)
    const roomName = this.carateObjectOfSelectedDay?.roomName;

    // roomname can be empty in some edge cases (e.g. sold out & no fallback room/only 1 room available)
    if (roomName) {
      return `${result} - ${roomName}`;
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomsTabTableData() {
    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('pricing_assistant.label.room_type'),
        valuePath: ROOMS_TAB_COLUMN_VALUE_PATHS.ROOM_TYPE,
        textAlign: 'left' as const,
        tdComponent: HotelPricingAssistantTableCellsTdWithBadge,
      },
      {
        name: this.intl.t('pricing_assistant.current_price'),
        valuePath: ROOMS_TAB_COLUMN_VALUE_PATHS.CURRENT_PRICE,
        textAlign: 'right' as const,
        minWidth: 100, // same as figma
      },
      {
        name: this.intl.t('pricing_assistant.label.recommended_price.short'),
        valuePath: ROOMS_TAB_COLUMN_VALUE_PATHS.RECOMMENDED_PRICE,
        textAlign: 'right' as const,
        minWidth: 120, // same as figma
        tdComponent: !this.hasRecommendation
          ? PrismTableCellsTdIcon
          : undefined,
      },
    ];

    // Sort room type recommendations: Base room is at top and the rest is sorted in ascending order of currentValue
    this.roomTypeRecommendations.sort((a, b) => {
      if (a.isBaseRoom && !b.isBaseRoom) {
        return -1;
      }

      if (!a.isBaseRoom && b.isBaseRoom) {
        return 1;
      }

      if (a.currentValue !== b.currentValue) {
        return a.currentValue - b.currentValue;
      }

      // sort by room name in case current values are same
      return a.roomName.localeCompare(b.roomName);
    });

    const rows = this.roomTypeRecommendations.map((roomTypeRecommendation) => {
      return {
        [ROOMS_TAB_COLUMN_VALUE_PATHS.ROOM_TYPE]: {
          formattedValue: roomTypeRecommendation.roomName, // custom room type
          badgeLabel: roomTypeRecommendation.isBaseRoom
            ? this.intl.t(
                'pricing_assistant.settings.panel.section.rooms.leading_room',
              )
            : null,
        },
        [ROOMS_TAB_COLUMN_VALUE_PATHS.CURRENT_PRICE]:
          roomTypeRecommendation.formattedCurrentValue,
        [ROOMS_TAB_COLUMN_VALUE_PATHS.RECOMMENDED_PRICE]: this.hasRecommendation
          ? roomTypeRecommendation.formattedRecommendedValue
          : { iconName: 'checkmark' }, // show checkmark icon if there is no recommendation
      };
    });

    return {
      columns,
      rows,
    };
  }

  get roomTypeRecommendations(): RoomTypeRecommendation[] {
    return this.args.model.pricing.roomTypeRecommendations;
  }

  // Disable push:
  // 1. If the user is not allowed to push prices -> viewer user
  // 2. Invalid new price value
  // 3. Autopilot price is being pushed or is pushed
  get sendToPMSButtonDisabled(): boolean {
    return (
      this.access.isPricingAssistantActionOrInputDisabled ||
      this.newPriceInputFieldErrorState.hasErrors ||
      this.isAutopilotPriceBeingPushedOrIsPushed
    );
  }

  // Show autopilot day action in case user has edit rights and autopilot is enabled in settings
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showAutopilotDayAction() {
    return (
      this.access.hasPricingAssistantSettingsEditRights &&
      this.hotelAndMarketService.hotel.paAutopilotSettings?.enabled &&
      !isDayInsideAutopilotLeadtimeRange(
        this.args.date,
        this.hotelAndMarketService.getLocalTime(),
        this.hotelAndMarketService.hotel.paAutopilotSettings,
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRecommendedPriceInEditState() {
    // Show edit state in case: user is editing the price or an overwritten price is being pushed
    return (
      this.args.isEditActive ||
      (this.carecommendationOfSelectedDay?.overwrittenValue &&
        this.carecommendationOfSelectedDay?.isBeingPushedOrIsPushed)
    );
  }

  // Show rooms tab in case PMS is integrated AND there are more than one room types in the hierarchy
  get showRoomsTab(): boolean {
    return (
      this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated &&
      this.roomTypeRecommendations.length > 1
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleTabIds() {
    const tabs = [DETAIL_TAB_PRICING_TABS.CALCULATION];

    if (this.showRoomsTab) {
      tabs.push(DETAIL_TAB_PRICING_TABS.ROOMS);
    }

    return tabs;
  }

  @action
  handlePushPriceButtonClick(): void {
    if (!this.carateObjectOfSelectedDay) {
      return;
    }

    // Disable autopilot for the day if it's enabled
    if (this.carateObjectOfSelectedDay?.autopilotEnabled) {
      void this.toggleAutopilotEnabledStateForADay(false);
    }

    let pricePushParams = {};

    if (
      this.newPriceValue &&
      this.newPriceValue !== this.newPriceInitialValue
    ) {
      // Case 1: Sending an overwritten/custom price
      void this.pricingAssistantHandleRecommendationActionsService?.handleCreateRecommendationActionWithOverwrittenValue(
        this.carateObjectOfSelectedDay,
        this.carecommendationOfSelectedDay,
        this.newPriceValue,
      );

      pricePushParams = generateRecommendationPushData(
        this.carecommendationOfSelectedDay,
        this.carateObjectOfSelectedDay,
        this.newPriceValue,
      );

      // Toggle this flag off as the user is done editing the price (since the push button was clicked)
      this.args.toggleEdit(false);
    } else if (this.carecommendationOfSelectedDay) {
      // Case 2: Sending recommended price
      void this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationAction(
        this.carecommendationOfSelectedDay,
        RECOMMENDATION_ACTION_STATES.ACCEPTED,
      );

      pricePushParams = generateRecommendationPushData(
        this.carecommendationOfSelectedDay,
        this.carateObjectOfSelectedDay,
      );
    }

    this.usageAnalytics.track('Price Recommendation Pushed Manually', {
      eventTrigger: 'day-detail-modal',
      product: 'pricing-manager',
      ...pricePushParams,
      daysOffsetFromToday: differenceInDays(
        this.args.date,
        this.hotelAndMarketService.getLocalTime().toDate(),
      ),
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async toggleAutopilotEnabledStateForADay(option: Option | boolean) {
    if (!this.carateObjectOfSelectedDay) {
      return null;
    }

    // Update carate local state to reflect the UI change instantaneously
    this.carateObjectOfSelectedDay.autopilotEnabled =
      !this.carateObjectOfSelectedDay.autopilotEnabled;

    trackAutopilotDayToggle(this.usageAnalytics, this.hotelAndMarketService, {
      date: this.args.date,
      enabled: this.carateObjectOfSelectedDay.autopilotEnabled,
      eventTrigger: 'day-detail-modal',
      product: 'pricing-manager',
    });

    // autopilotEnabled also exists on the carecommendation model, so we need to update that as well (if there's a recommendation)
    // Our autopilot state checks rely on the autopilotEnabled on both carate & carecommendation models
    if (this.carateObjectOfSelectedDay.carecommendation) {
      this.carateObjectOfSelectedDay.carecommendation.autopilotEnabled =
        !this.carateObjectOfSelectedDay.carecommendation.autopilotEnabled;
    }

    // Make BE request to update autopilot state
    const result =
      await this.pricingAssistantDataRequestService.saveAndUpdateAutopilotEnabledStateForADayTask.perform(
        this.args.date,
        this.carateObjectOfSelectedDay.autopilotEnabled,
      );

    const showToastMessage = Boolean(option);

    // Show appropriate toast message on success
    if (result.isSuccess && showToastMessage) {
      const formattedSelectedDay = dayjs(this.args.date).format(
        this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
      );

      const toastOptions = {
        date: formattedSelectedDay,
        class: 'text-300--bold',
        htmlSafe: true,
      };

      const { autopilotEnabled } = this.carateObjectOfSelectedDay;

      if (autopilotEnabled) {
        this.toast.success(
          this.intl.t(
            'pricing_assistant.autopilot.toast.message.enable_autopilot.for_a_day',
            toastOptions,
          ),
          {
            title: this.intl.t(
              'pricing_assistant.autopilot.toast.title.enable_autopilot',
            ),
            showIcon: true,
          },
        );
      } else {
        this.toast.success(
          this.intl.t(
            'pricing_assistant.autopilot.toast.message.disable_autopilot.for_a_day',
            toastOptions,
          ),
          {
            title: this.intl.t(
              'pricing_assistant.autopilot.toast.title.disable_autopilot',
            ),
            showIcon: true,
          },
        );
      }
    }

    // Rollback local state in case of failure
    if (!result.isSuccess) {
      this.carateObjectOfSelectedDay.autopilotEnabled =
        !this.carateObjectOfSelectedDay.autopilotEnabled;

      // Same for carecommendation model
      if (this.carateObjectOfSelectedDay.carecommendation) {
        this.carateObjectOfSelectedDay.carecommendation.autopilotEnabled =
          !this.carateObjectOfSelectedDay.carecommendation.autopilotEnabled;
      }
    }

    return result;
  }

  @action
  toggleRecommendedPriceEditState(isShown: boolean): void {
    this.args.toggleEdit(isShown);

    if (isShown) {
      this.usageAnalytics.track('Price Editing', {
        eventTrigger: 'day-detail-modal',
        product: 'pricing-manager',
        daysOffsetFromToday: differenceInDays(
          this.args.date,
          this.hotelAndMarketService.getLocalTime().toDate(),
        ),
      });
      // initialize the input with a value that makes sense
      this.newPriceValue = this.newPriceInitialValue;
    } else {
      // clear out new price value
      this.newPriceValue = null;
    }
  }

  @action
  updateNewPrice(value: number | null): void {
    this.newPriceValue = value;
  }

  @action
  updateSelectedTab(tabId: string): void {
    this.selectedTabId = tabId;
  }

  <template>
    {{! template-lint-disable no-duplicate-id }}
    <div class="flex h-full flex-col">
      <GeneralModalFilterBar class="flex justify-between">
        <div class="modal-filter-bar__filter-text truncate">
          {{displayRateParityFilters
            @filteredRatesParamsForPricingAssistant
            @currentHotel
          }}
        </div>
      </GeneralModalFilterBar>

      <div class={{styles.tab}}>
        {{#unless @isLoading}}
          <PrismCard @contentLayout="noPadding" class={{styles.main-card}}>
            <:header as |Header|>
              <Header.title>
                {{t "pricing_assistant.general.recommendation"}}
              </Header.title>
            </:header>
            <:headerAfter>
              {{#if this.showAutopilotDayAction}}
                {{#let
                  (component
                    (ensureSafeComponent this.triggerDropdownIconButton)
                    buttonType="secondary"
                    icon="kebab"
                    isDisabled=this.carecommendationOfSelectedDay.isBeingPushedOrIsPushed
                  )
                  as |triggerComponent|
                }}
                  <PrismComboSelectInput
                    @disabled={{this.carecommendationOfSelectedDay.isBeingPushedOrIsPushed}}
                    @horizontalPosition="right"
                    {{! @glint-expect-error: Incorrect type }}
                    @onChange={{this.toggleAutopilotEnabledStateForADay}}
                    {{! @glint-expect-error: Incorrect type }}
                    @options={{this.autopilotDayAction}}
                    @unsafe_triggerComponent={{triggerComponent}}
                    {{! @glint-expect-error: Incorrect type }}
                    @value={{this.autopilotDayActionValue}}
                    {{! Prevent user from changing the autopilot state while the price is being pushed }}
                  />
                {{/let}}
              {{/if}}
            </:headerAfter>
            <:content>
              <div class={{styles.content}}>
                <PrismTabsContainer
                  @activeTabId={{this.selectedTabId}}
                  @onSelectTab={{this.updateSelectedTab}}
                  @visibleTabIds={{this.visibleTabIds}}
                  as |TabsContainer|
                >
                  <TabsContainer.header>
                    <:default as |Header|>
                      <Header.tab
                        @id={{this.DETAIL_TAB_PRICING_TABS.CALCULATION}}
                      >
                        {{t "pricing_assistant.label.calculation"}}
                      </Header.tab>
                      <Header.tab
                        @id={{this.DETAIL_TAB_PRICING_TABS.ROOMS}}
                        data-test-id="pa-day-detail-rooms-tab"
                      >
                        {{t "pricing_assistant.label.rooms"}}
                      </Header.tab>
                    </:default>
                  </TabsContainer.header>

                  <TabsContainer.content as |Content|>
                    <Content.tab
                      @id={{this.DETAIL_TAB_PRICING_TABS.CALCULATION}}
                    >
                      <div
                        class="{{styles.scrollable-container}}
                          flex flex-col gap-400"
                      >
                        {{#if this.hasRecommendation}}
                          <div
                            class="{{styles.current-price-container}}
                              flex items-center justify-between"
                          >
                            <h4>
                              {{#if
                                (gt
                                  @filteredRatesParamsForPricingAssistant.los 1
                                )
                              }}
                                {{t "pricing_assistant.avg_current_price"}}
                                ({{t
                                  "rates.nights"
                                  count=@filteredRatesParamsForPricingAssistant.los
                                }})
                              {{else}}
                                {{t "pricing_assistant.current_price"}}
                              {{/if}}
                            </h4>
                            <h4>{{this.carateObjectOfSelectedDay.formattedRate}}
                              {{#if
                                this.carateObjectOfSelectedDay.hasValueAndMessage
                              }}
                                ({{this.carateObjectOfSelectedDay.formattedMessage}})
                              {{/if}}
                            </h4>
                          </div>
                        {{else}}
                          <div class="flex items-center justify-center">
                            <PrismMessage
                              @illustration="100x100_success"
                              @title={{t
                                "pricing_assistant.no_recommendation.empty_state.title"
                              }}
                            />
                          </div>
                        {{/if}}

                        <div class="flex flex-col gap-200">
                          {{#if this.carecommendationOfSelectedDay}}
                            {{! Occupancy reasons }}
                            {{#if
                              this.carecommendationOfSelectedDay.paRecommendationReason.hasOccupancyReasons
                            }}
                              <HotelPricingAssistantDayDetailTabsOccupancyRecommendationReasons
                                @carecommendationOfSelectedDay={{this.carecommendationOfSelectedDay}}
                              />
                            {{/if}}

                            {{! Compset reasons }}
                            {{#if
                              this.carecommendationOfSelectedDay.paRecommendationReason.hasCompsetReasons
                            }}
                              <HotelPricingAssistantDayDetailTabsCompsetRecommendationReasons
                                @carecommendationOfSelectedDay={{this.carecommendationOfSelectedDay}}
                                @date={{@date}}
                              />
                            {{/if}}

                            {{! User rules reasons }}
                            {{#if
                              this.carecommendationOfSelectedDay.paRecommendationReason.hasUserRulesReasons
                            }}
                              <HotelPricingAssistantDayDetailTabsUserRulesRecommendationReasons
                                @carecommendationOfSelectedDay={{this.carecommendationOfSelectedDay}}
                              />
                            {{/if}}
                          {{/if}}

                          {{! Events and Holidays }}
                          {{#if @dayModel}}
                            <HotelPricingAssistantDayDetailTabsEventsAndHolidaysList
                              @dayModel={{@dayModel}}
                            />
                          {{/if}}
                        </div>
                      </div>
                    </Content.tab>

                    <Content.tab @id={{this.DETAIL_TAB_PRICING_TABS.ROOMS}}>
                      <div
                        class="{{styles.scrollable-container}}
                          flex flex-col gap-400"
                      >
                        <div>
                          <PrismTable
                            @columns={{this.roomsTabTableData.columns}}
                            @fillMode="first-column"
                            @rows={{this.roomsTabTableData.rows}}
                            @widthConstraint="eq-container"
                          />
                        </div>
                      </div>
                    </Content.tab>
                  </TabsContainer.content>
                </PrismTabsContainer>
              </div>
            </:content>

            <:footer>
              <div
                class={{styles.footer}}
                {{! Reset recommended price edit state (close the input field and clear out overwritten value) on day change }}
                {{didUpdate
                  (fn this.toggleRecommendedPriceEditState false)
                  @date
                }}
              >
                {{#if
                  this.carecommendationOfSelectedDay.isBeingPushedOrIsPushed
                }}
                  <div class={{styles.loading-bar}}>
                    <PrismLoadingBar />
                  </div>
                {{/if}}

                {{#if this.carecommendationOfSelectedDay.hasException}}
                  <div class="mb-200">
                    <PrismBanner
                      @bannerMessage={{this.pushErrorMessageTranslation}}
                      @bannerStyle="critical"
                      @bannerTitle={{t
                        "pricing_assistant.exception.action_state.title"
                      }}
                      @position="island"
                    />
                  </div>
                {{else if
                  (and
                    this.showRecommendedPriceInEditState
                    this.carateObjectOfSelectedDay.autopilotEnabled
                  )
                }}
                  <div class="mb-200">
                    <PrismBanner
                      @bannerMessage={{t
                        "pricing_assistant.autopilot.edit_price_banner.message"
                      }}
                      @bannerStyle="warning"
                      @bannerTitle={{t
                        "pricing_assistant.autopilot.edit_price_banner.title"
                      }}
                      @position="island"
                    />
                  </div>
                {{/if}}

                {{#if
                  (and
                    this.showRecommendedPriceInEditState
                    this.carateObjectOfSelectedDay
                  )
                }}
                  <HotelPricingAssistantEditPriceNumberInput
                    {{! @glint-expect-error: Incorrect type }}
                    @carateObject={{this.carateObjectOfSelectedDay}}
                    @newPriceInputFieldErrorState={{this.newPriceInputFieldErrorState}}
                    @newPriceValue={{this.newPriceValue}}
                    @recommendationObject={{this.carecommendationOfSelectedDay}}
                    @updateNewPrice={{this.updateNewPrice}}
                  />
                {{else}}
                  <div class="{{styles.price-container}} flex flex-col gap-100">
                    <div
                      class="price-info flex items-center justify-between
                        {{if
                          this.hasRecommendation
                          styles.price-info-recommendation
                        }}
                        {{if
                          this.carateObjectOfSelectedDay.autopilotEnabled
                          styles.price-info-autopilot
                        }}"
                    >
                      {{#if this.hasRecommendation}}
                        {{#if
                          this.carecommendationOfSelectedDay.autopilotEnabled
                        }}
                          <div class="flex flex-row items-center gap-100">
                            <PrismIcon
                              @color="inherit"
                              @name="automation"
                              @size="large"
                            />
                            <h4>{{t "pricing_assistant.autopilot_price"}}</h4>
                          </div>
                          <span
                            class="text-400--bold text-nowrap"
                          >{{this.carecommendationOfSelectedDay.formattedRecommendedValue}}</span>
                        {{else if
                          this.carecommendationOfSelectedDay.isRecommendedPriceOverwritten
                        }}
                          <h4>{{t
                              "pricing_assistant.label.new_updated_price"
                            }}</h4>

                          <p class="flex flex-row gap-100">
                            <span
                              class="text-400--regular strikethrough-text text-nowrap"
                            >{{this.carecommendationOfSelectedDay.formattedRecommendedValue}}</span>
                            <span
                              class="text-400--bold text-nowrap"
                            >{{this.carecommendationOfSelectedDay.formattedOverwrittenValue}}</span>
                          </p>
                        {{else}}
                          <h4>
                            {{#if
                              (gt @filteredRatesParamsForPricingAssistant.los 1)
                            }}
                              {{t "pricing_assistant.avg_recommended_price"}}
                              ({{t
                                "rates.nights"
                                count=@filteredRatesParamsForPricingAssistant.los
                              }})
                            {{else}}
                              {{t "pricing_assistant.label.recommended_price"}}
                            {{/if}}
                          </h4>
                          <span
                            class="text-400--bold text-nowrap"
                          >{{this.carecommendationOfSelectedDay.formattedRecommendedValue}}</span>
                        {{/if}}
                      {{else}}
                        {{#if this.carateObjectOfSelectedDay.autopilotEnabled}}
                          <div class="flex flex-row items-center gap-100">
                            <PrismIcon
                              @color="inherit"
                              @name="automation"
                              @size="large"
                            />
                            <h4>{{t "pricing_assistant.autopilot_price"}}</h4>
                          </div>
                        {{else}}
                          <h4>
                            {{#if
                              (gt @filteredRatesParamsForPricingAssistant.los 1)
                            }}
                              {{t "pricing_assistant.avg_current_price"}}
                              ({{t
                                "rates.nights"
                                count=@filteredRatesParamsForPricingAssistant.los
                              }})
                            {{else}}
                              {{t "pricing_assistant.current_price"}}
                            {{/if}}
                          </h4>
                        {{/if}}
                        <span
                          class="text-400--bold text-nowrap"
                        >{{this.carateObjectOfSelectedDay.formattedRate}}
                          {{#if
                            this.carateObjectOfSelectedDay.hasValueAndMessage
                          }}
                            ({{this.carateObjectOfSelectedDay.formattedMessage}})
                          {{/if}}</span>

                      {{/if}}
                    </div>

                    {{#if this.carateObjectOfSelectedDay.autopilotEnabled}}
                      {{! Hide autopilot push message if autopilot day has exception }}
                      {{#unless
                        (and
                          this.hasRecommendation
                          this.carecommendationOfSelectedDay.hasException
                        )
                      }}
                        <p class="text-300--regular">
                          {{this.autopilotPushMessageTranslation}}
                        </p>
                      {{/unless}}
                    {{else}}
                      <p class="text-300--regular">
                        {{this.recommendedPriceFiltersTranslation}}
                      </p>
                    {{/if}}
                  </div>
                {{/if}}

                {{#if this.access.hasPricingAssistantViewRights}}
                  <div
                    class="{{styles.cta-container}}
                      mt-200 flex flex-row gap-200"
                  >
                    {{#if
                      (and
                        this.hasRecommendation
                        (not
                          this.carecommendationOfSelectedDay.autopilotEnabled
                        )
                      )
                    }}
                      {{#if
                        this.carecommendationOfSelectedDay.isBeingPushedOrIsPushed
                      }}
                        <HotelPricingAssistantRecommendationActionState
                          @hasSmallText={{true}}
                          @recommendationObject={{this.carecommendationOfSelectedDay}}
                          @showPriceChangeDetails={{true}}
                        />
                      {{else}}
                        {{#if this.showRecommendedPriceInEditState}}
                          <PrismButton
                            @buttonType="secondary"
                            @label={{t "general.cancel"}}
                            @onClick={{fn
                              this.toggleRecommendedPriceEditState
                              false
                            }}
                            class="w-1/2"
                          />
                        {{else}}
                          <div class="{{styles.cta-action-button}} w-1/2">
                            <PrismButton
                              @buttonType="secondary"
                              @iconBefore="edit-pencil"
                              @isDisabled={{this.access.isPricingAssistantActionOrInputDisabled}}
                              @label={{t "pricing_assistant.label.edit_price"}}
                              @onClick={{fn
                                this.toggleRecommendedPriceEditState
                                true
                              }}
                            />
                            <HotelPricingAssistantRecommendationActionButtonTooltip
                              @buttonType={{this.EDIT_RECOMMENDED_PRICE_BUTTON_TYPE}}
                              @showTooltipIfActionButtonsEnabled={{false}}
                            />
                          </div>
                        {{/if}}
                        <div class="{{styles.cta-action-button}} w-1/2">
                          <PrismButton
                            @iconBefore="send"
                            @isDisabled={{this.sendToPMSButtonDisabled}}
                            @label={{t "pricing_assistant.label.push"}}
                            @onClick={{this.handlePushPriceButtonClick}}
                          />
                          <HotelPricingAssistantRecommendationActionButtonTooltip
                            @buttonType={{this.RECOMMENDATIONACTIONS_ACTIONS.ACCEPTED}}
                            @showTooltipIfActionButtonsEnabled={{false}}
                          />
                        </div>
                      {{/if}}
                    {{else}}
                      {{#if this.showRecommendedPriceInEditState}}
                        <PrismButton
                          @buttonType="secondary"
                          @label={{t "general.cancel"}}
                          @onClick={{fn
                            this.toggleRecommendedPriceEditState
                            false
                          }}
                          class="w-1/2"
                        />
                        <div class="{{styles.cta-action-button}} w-1/2">
                          <PrismButton
                            @iconBefore="send"
                            @isDisabled={{this.sendToPMSButtonDisabled}}
                            @label={{t "pricing_assistant.label.push"}}
                            @onClick={{this.handlePushPriceButtonClick}}
                          />
                          <HotelPricingAssistantRecommendationActionButtonTooltip
                            @buttonType={{this.RECOMMENDATIONACTIONS_ACTIONS.ACCEPTED}}
                            @showTooltipIfActionButtonsEnabled={{false}}
                          />
                        </div>
                      {{else}}
                        <div class="{{styles.cta-action-button}} w-full">
                          <PrismButton
                            @buttonType="secondary"
                            @iconBefore="edit-pencil"
                            @isDisabled={{this.editPriceButtonDisabled}}
                            @label={{t "pricing_assistant.label.edit_price"}}
                            @onClick={{fn
                              this.toggleRecommendedPriceEditState
                              true
                            }}
                          />
                          <HotelPricingAssistantRecommendationActionButtonTooltip
                            @buttonType={{this.EDIT_RECOMMENDED_PRICE_BUTTON_TYPE}}
                            @showTooltipIfActionButtonsEnabled={{false}}
                          />
                        </div>
                      {{/if}}
                    {{/if}}
                  </div>
                {{/if}}
              </div>
            </:footer>
          </PrismCard>
        {{/unless}}
      </div>
    </div>
  </template>
}
